import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "wf:chunk-reloaded-at";

/**
 * After a new deploy, hashed chunk files from the previous build disappear.
 * Browsers holding the old HTML then throw
 * "Failed to fetch dynamically imported module" 404s.
 *
 * This wrapper retries the import once (cache-busted) and, if it still fails,
 * performs a single hard reload so the client picks up the fresh manifest.
 * Guarded so we can never loop.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      // Second attempt — transient network blips resolve here.
      try {
        return await factory();
      } catch {
        if (typeof window !== "undefined") {
          let last = 0;
          try {
            last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
          } catch {}
          if (Date.now() - last > 60_000) {
            try {
              window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
            } catch {}
            window.location.reload();
          }
        }
        // Render nothing rather than crashing the tree.
        return { default: (() => null) as unknown as T };
      }
    }
  });
}
